import { BookMarked, BrainCircuit, House, LockKeyhole, Map, ShieldCheck } from "lucide-react";
import type { ReactNode } from "react";

interface AgeBandPlan {
  band: string;
  title: string;
  copy: string;
  theme: string;
}

interface MarketingSectionsProps {
  ageBandPlan: AgeBandPlan[];
}

export function MarketingSections({ ageBandPlan }: MarketingSectionsProps) {
  return (
    <>
      <section className="evidence-section" id="why" aria-labelledby="why-title">
        <div className="section-heading">
          <div>
            <h2 id="why-title">Built on how children actually learn.</h2>
            <p>
              Nesty Learning combines structured reading instruction with concept-first maths, short lessons,
              cumulative review, and progress tracking that adapts to each child.
            </p>
          </div>
        </div>
        <div className="principle-grid">
          <Principle icon={<BookMarked size={23} />} title="Reading is more than phonics" copy="Word reading, language comprehension, vocabulary, fluency, story, and reading for pleasure move together." />
          <Principle icon={<BrainCircuit size={23} />} title="Maths is concept-led" copy="Small steps use representations, mathematical language, retrieval, reasoning, and problem solving." />
          <Principle icon={<Map size={23} />} title="Progression is explainable" copy="The engine watches accuracy, support reliance, speed, frustration signals, and review stability." />
        </div>
      </section>

      <section className="age-section" id="ages" aria-labelledby="ages-title">
        <div className="section-heading">
          <div>
            <h2 id="ages-title">Ages 3 to 13, skill-based instead of age-locked.</h2>
            <p>
              Younger children get guided shared use. Older children who need catch-up see age-respectful themes and serious learning.
            </p>
          </div>
        </div>
        <div className="age-timeline">
          {ageBandPlan.map((item) => (
            <article key={item.band}>
              <span>{item.band}</span>
              <h3>{item.title}</h3>
              <p>{item.copy}</p>
              <strong>{item.theme}</strong>
            </article>
          ))}
        </div>
      </section>

      <section className="parent-section" aria-labelledby="parent-title">
        <div className="parent-copy">
          <h2 id="parent-title">See what changed, not just what they tapped.</h2>
          <p>
            Track secure skills, fragile skills, effort, focus, and what to do next at home. Every substantial lesson ends with a simple off-screen card for shared family learning.
          </p>
        </div>
        <div className="report-preview" aria-label="Parent report preview">
          <article>
            <span className="mini-icon" aria-hidden="true">
              <House size={20} />
            </span>
            <strong>Next at home</strong>
            <p>Build 7 in two ways with blocks, then say the parts aloud.</p>
          </article>
          <article>
            <span className="mini-icon" aria-hidden="true">
              <ShieldCheck size={20} />
            </span>
            <strong>Clear support need</strong>
            <p>Accurate with audio, less secure without it. Fade support slowly.</p>
          </article>
        </div>
      </section>

      <section className="safety-section" id="safety" aria-labelledby="safety-title">
        <div>
          <h2 id="safety-title">Calm by design.</h2>
          <p>No ads. No open chat. No wandering links. Just bounded, parent-led learning.</p>
        </div>
        <div className="safety-list">
          <span>
            <LockKeyhole size={19} aria-hidden="true" /> Parent-created accounts
          </span>
          <span>
            <ShieldCheck size={19} aria-hidden="true" /> Minimal data collection
          </span>
          <span>
            <BrainCircuit size={19} aria-hidden="true" /> Evidence-informed, not overclaimed
          </span>
        </div>
      </section>

      <section className="final-cta" aria-labelledby="cta-title">
        <h2 id="cta-title">A learning home for the Nesty family experience.</h2>
        <p>Not a dyslexia-only site. Not a random games site. A structured learning home for every child.</p>
        <a className="primary-button" href="#join-beta">Join free beta</a>
      </section>
    </>
  );
}

function Principle({ copy, icon, title }: { copy: string; icon: ReactNode; title: string }) {
  return (
    <article>
      <span className="principle-icon" aria-hidden="true">{icon}</span>
      <h3>{title}</h3>
      <p>{copy}</p>
    </article>
  );
}
